/**
 * chainConsolidator.ts - Consolidates multiple inference chains into a single deduplicated graph
 *
 * Transforms raw InferenceChain[] into a ConsolidatedGraph with:
 * - Zero node duplication (each concept appears exactly once)
 * - Zero edge duplication (each source→target pair appears once)
 * - chainRefs counting how many original chains reference each node/edge
 * - BSC perspective grouping for columnar layout
 */

import type {
  InferenceChain,
  BSCPerspective,
  ConsolidatedNode,
  ConsolidatedEdge,
  ConsolidatedGraph,
  ConsolidatedNodeType,
} from '@models/oracle'

// Canonicalize node IDs to always end with _id suffix
// Backend produces anchor_concept = "customer_retention" (no suffix) while
// matched_nodes = ["customer_retention_id", ...] (with suffix). Without
// normalization these create two separate Map entries → duplicate nodes.
export function normalizeNodeId(id: string): string {
  if (id.startsWith('gap:')) return id  // gap IDs use their own namespace
  return id.replace(/_id$/, '') + '_id'
}

// BSC perspective inference from concept/node_id keywords
function inferPerspective(concept: string): BSCPerspective | 'unknown' {
  const lower = concept.toLowerCase()

  if (/training|employee|skill|culture|capital|knowledge|technology|system|human|org_capital|information|learning/.test(lower)) {
    return 'Learning_Growth'
  }
  if (/process|operation|efficiency|quality|service|delivery|innovation|production|supply|logistics|compliance/.test(lower)) {
    return 'Internal_Process'
  }
  if (/customer|client|retention|satisfaction|loyalty|churn|acquisition|relationship|brand|market_share/.test(lower)) {
    return 'Customer'
  }
  if (/revenue|profit|cost|cash|financial|margin|receivable|payable|income|shareholder|debt|equity|asset|budget|earning/.test(lower)) {
    return 'Financial'
  }

  return 'unknown'
}

// Detect node type from ID prefix
function detectNodeType(nodeId: string): ConsolidatedNodeType {
  if (nodeId.startsWith('metric_')) return 'metric'
  if (nodeId.startsWith('framework_')) return 'framework'
  return 'biz_ontology'
}

// Clean concept name from node_id
function cleanConceptName(nodeId: string): string {
  return nodeId.replace(/_id$/, '').replace(/_/g, ' ')
}

// Known ontology concept patterns — anchors matching these are likely valid
const ONTOLOGY_PATTERNS = /customer|revenue|profit|cost|retention|satisfaction|loyalty|churn|brand|market|product|service|process|operation|efficiency|quality|employee|training|skill|culture|capital|technology|innovation|growth|financial|cash|margin|supply|logistics|delivery|compliance|acquisition|relationship/

/**
 * Check if an anchor concept is valid by verifying it appears in at least one
 * chain's matched_nodes or matches a known ontology pattern.
 */
function isValidAnchor(anchorId: string, chains: InferenceChain[]): boolean {
  // Check if the anchor appears in any chain's matched_nodes
  for (const chain of chains) {
    const normalizedMatched = chain.matched_nodes.map(id => normalizeNodeId(id))
    if (normalizedMatched.includes(anchorId)) return true
  }
  // Fallback: check if it matches known ontology concepts
  return ONTOLOGY_PATTERNS.test(anchorId.toLowerCase())
}

/**
 * Consolidate an array of inference chains into a single deduplicated graph.
 */
export function consolidateChains(chains: InferenceChain[]): ConsolidatedGraph {
  const nodeMap = new Map<string, ConsolidatedNode>()
  const edgeMap = new Map<string, ConsolidatedEdge>()

  // Track which node_ids are anchor concepts
  const anchorIds = new Set<string>()

  chains.forEach((chain, chainIndex) => {
    // Normalize anchor ID to canonical _id form
    let anchorId = normalizeNodeId(chain.anchor_concept)

    // Safety net: if anchor is invalid (e.g., "my_id"), use first matched_node instead
    if (!isValidAnchor(anchorId, chains) && chain.matched_nodes.length > 0) {
      anchorId = normalizeNodeId(chain.matched_nodes[0])
    }

    anchorIds.add(anchorId)
    registerNode(nodeMap, anchorId, chainIndex, true, false)

    // Normalize + deduplicate matched_nodes within this chain
    const seen = new Set<string>([anchorId])
    const normalizedMatched = chain.matched_nodes
      .map(id => normalizeNodeId(id))
      .filter(id => {
        if (seen.has(id)) return false
        seen.add(id)
        return true
      })

    // Register matched nodes and edges
    normalizedMatched.forEach((nodeId, i) => {
      registerNode(nodeMap, nodeId, chainIndex, false, false)

      // Pairwise adjacency: previous node → this node
      const sourceId = i === 0 ? anchorId : normalizedMatched[i - 1]
      const relationship = chain.direction === 'upstream' ? 'enables' : 'drives'
      registerEdge(edgeMap, sourceId, nodeId, chainIndex, relationship)
    })

    // Register gap nodes
    chain.gaps.forEach((gap, i) => {
      const gapId = `gap:${gap}`
      registerNode(nodeMap, gapId, chainIndex, false, true)

      // Edge from last matched node (or anchor) to gap
      const lastMatched = normalizedMatched.length > 0
        ? normalizedMatched[normalizedMatched.length - 1]
        : anchorId
      if (i === 0) {
        registerEdge(edgeMap, lastMatched, gapId, chainIndex, 'gap_link')
      } else {
        registerEdge(edgeMap, `gap:${chain.gaps[i - 1]}`, gapId, chainIndex, 'gap_link')
      }
    })
  })

  // Mark anchors
  anchorIds.forEach(id => {
    const node = nodeMap.get(id)
    if (node) node.isAnchor = true
  })

  const nodes = Array.from(nodeMap.values())
  const edges = Array.from(edgeMap.values())

  // Group nodes by BSC perspective
  const bscColumns: Record<BSCPerspective | 'unknown', ConsolidatedNode[]> = {
    'Learning_Growth': [],
    'Internal_Process': [],
    'Customer': [],
    'Financial': [],
    'unknown': [],
  }

  nodes.forEach(node => {
    bscColumns[node.perspective].push(node)
  })

  // Sort within each BSC group by chainRefs descending (initial order, Dagre will refine)
  Object.values(bscColumns).forEach(col => {
    col.sort((a, b) => b.chainRefs - a.chainRefs)
  })

  return {
    nodes,
    edges,
    bscColumns,
    totalOriginalChains: chains.length,
  }
}

function registerNode(
  map: Map<string, ConsolidatedNode>,
  nodeId: string,
  chainIndex: number,
  isAnchor: boolean,
  isGap: boolean,
): void {
  const existing = map.get(nodeId)
  if (existing) {
    existing.chainRefs++
    if (!existing.sourceChainIndices.includes(chainIndex)) {
      existing.sourceChainIndices.push(chainIndex)
    }
    if (isAnchor) existing.isAnchor = true
  } else {
    const cleanId = isGap ? nodeId.replace(/^gap:/, '') : nodeId
    map.set(nodeId, {
      id: nodeId,
      concept: cleanConceptName(cleanId),
      perspective: inferPerspective(cleanId),
      isGap,
      isAnchor,
      chainRefs: 1,
      sourceChainIndices: [chainIndex],
      nodeType: isGap ? 'gap' : detectNodeType(nodeId),
    })
  }
}

function registerEdge(
  map: Map<string, ConsolidatedEdge>,
  sourceId: string,
  targetId: string,
  _chainIndex: number,
  relationship: string,
): void {
  // Normalize edge endpoint IDs for consistent lookup
  const normSource = normalizeNodeId(sourceId)
  const normTarget = normalizeNodeId(targetId)
  const key = `${normSource}→${normTarget}`
  const existing = map.get(key)
  if (existing) {
    existing.chainRefs++
  } else {
    map.set(key, {
      sourceId: normSource,
      targetId: normTarget,
      chainRefs: 1,
      relationship,
    })
  }
}
